/**
 * Streak Service
 * Handles streak tracking for daily diary logging
 * Stores streak data in fsnap_users/{userId} document fields
 */

import { format, subDays, parseISO, isValid } from 'date-fns';
import { FirestoreService } from './firebase/firestore';
import { Firebase } from './firebase';
import { ANALYTICS_EVENTS } from './firebase/config';

/**
 * Streak data stored on user profile
 */
export interface StreakData {
  current: number;
  best: number;
  lastLogDate: string; // 'YYYY-MM-DD'
}

/**
 * Result from updating a streak
 */
export interface StreakUpdateResult {
  streakData: StreakData;
  streakIncremented: boolean;
}

// Streak milestones for celebrations
export const STREAK_MILESTONES = [
  { days: 3, emoji: '\uD83D\uDD25', titleKey: 'streak.milestones.3days' },
  { days: 7, emoji: '\u2B50', titleKey: 'streak.milestones.7days' },
  { days: 14, emoji: '\uD83C\uDF1F', titleKey: 'streak.milestones.14days' },
  { days: 30, emoji: '\uD83C\uDFC6', titleKey: 'streak.milestones.30days' },
  { days: 60, emoji: '\uD83D\uDC8E', titleKey: 'streak.milestones.60days' },
  { days: 100, emoji: '\uD83D\uDC51', titleKey: 'streak.milestones.100days' },
] as const;

// Default streak data for new users
export const DEFAULT_STREAK_DATA: StreakData = {
  current: 0,
  best: 0,
  lastLogDate: '',
};

/**
 * Get today's date string in YYYY-MM-DD format
 */
function getTodayDateString(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Get yesterday's date string in YYYY-MM-DD format
 */
function getYesterdayDateString(): string {
  return format(subDays(new Date(), 1), 'yyyy-MM-dd');
}

/**
 * Check if a date string is valid
 */
function isValidDateString(dateString: string): boolean {
  if (!dateString) return false;
  const parsed = parseISO(dateString);
  return isValid(parsed);
}

/**
 * Pure display helper: the streak count that should be SHOWN right now.
 *
 * The stored `streak.current` only resets lazily (when updateStreak/getStreak
 * next runs), so a user who last logged 3+ days ago can still carry a stale
 * `current` on their profile. This helper applies the same local-date rule
 * without any I/O: if `lastLogDate` is today or yesterday the streak is
 * alive → return `current`; otherwise (older, missing, or invalid) → 0.
 *
 * Intended for UI consumers (StreakBadge / StreakModal) that read the streak
 * straight off the real-time profile.
 */
export function getEffectiveStreak(
  streak: { current: number; best: number; lastLogDate: string } | null | undefined
): number {
  if (!streak || !isValidDateString(streak.lastLogDate)) return 0;

  // 'YYYY-MM-DD' strings compare correctly lexicographically.
  // Older than yesterday (local dates) — streak is broken.
  if (streak.lastLogDate < getYesterdayDateString()) return 0;

  return streak.current || 0;
}

export class StreakService {
  /**
   * Update streak when user adds a diary entry.
   * Call this after any diary entry is created.
   *
   * Logic:
   * - If lastLogDate is today, no change
   * - If lastLogDate is yesterday, increment current streak
   * - If lastLogDate is older than yesterday (or invalid), reset to 1
   * - Update best if current exceeds it
   */
  static async updateStreak(userId: string): Promise<StreakUpdateResult> {
    const today = getTodayDateString();
    const yesterday = getYesterdayDateString();

    // Get current streak data from user profile
    const profile = await FirestoreService.getUserProfile(userId);
    const streakData: StreakData = profile?.streak || { ...DEFAULT_STREAK_DATA };

    // Already logged today - no change needed
    if (streakData.lastLogDate === today) {
      return {
        streakData,
        streakIncremented: false,
      };
    }

    let updatedStreakData: StreakData;

    if (streakData.lastLogDate === yesterday) {
      // Continuing streak from yesterday
      const newCurrent = streakData.current + 1;
      updatedStreakData = {
        current: newCurrent,
        best: Math.max(newCurrent, streakData.best),
        lastLogDate: today,
      };
    } else if (
      !isValidDateString(streakData.lastLogDate) ||
      streakData.lastLogDate < yesterday
    ) {
      // Streak broken or first time logging - start fresh at 1
      updatedStreakData = {
        current: 1,
        best: Math.max(1, streakData.best),
        lastLogDate: today,
      };
    } else {
      // Future date edge case - no change
      return {
        streakData,
        streakIncremented: false,
      };
    }

    // Update Firestore (streak is a nested field on the user document)
    await FirestoreService.updateUserProfile(userId, {
      streak: updatedStreakData,
    } as any);

    // Fire analytics event when a milestone boundary is crossed
    const newStreak = updatedStreakData.current;
    if (STREAK_MILESTONES.some((m) => m.days === newStreak)) {
      Firebase.analytics.logEvent(ANALYTICS_EVENTS.STREAK_MILESTONE, { days: newStreak });
    }

    return {
      streakData: updatedStreakData,
      streakIncremented: true,
    };
  }

  /**
   * Get current streak data for a user.
   * Returns { current, best, lastLogDate }.
   * Also checks if the streak should be considered broken
   * (e.g. user opens app and last log was before yesterday).
   */
  static async getStreak(
    userId: string
  ): Promise<StreakData> {
    const yesterday = getYesterdayDateString();
    const today = getTodayDateString();

    const profile = await FirestoreService.getUserProfile(userId);
    const streakData: StreakData = profile?.streak || { ...DEFAULT_STREAK_DATA };

    // If no previous logging, return defaults
    if (!isValidDateString(streakData.lastLogDate)) {
      return streakData;
    }

    // If last log is today or yesterday, streak is still active
    if (streakData.lastLogDate === today || streakData.lastLogDate === yesterday) {
      return streakData;
    }

    // Last log is older than yesterday - streak is broken, reset current to 0
    if (streakData.current > 0) {
      const resetData: StreakData = {
        ...streakData,
        current: 0,
      };

      // Persist the reset
      await FirestoreService.updateUserProfile(userId, {
        streak: resetData,
      } as any);

      return resetData;
    }

    return streakData;
  }

  /**
   * Get the next milestone for the current streak count
   */
  static getNextMilestone(
    currentStreak: number
  ): (typeof STREAK_MILESTONES)[number] | null {
    return STREAK_MILESTONES.find((m) => m.days > currentStreak) || null;
  }

  /**
   * Get progress to next milestone (0-100)
   */
  static getMilestoneProgress(currentStreak: number): number {
    const nextMilestone = this.getNextMilestone(currentStreak);
    if (!nextMilestone) return 100;

    const previousMilestone = [...STREAK_MILESTONES]
      .reverse()
      .find((m) => m.days <= currentStreak);

    const startDays = previousMilestone?.days || 0;
    const endDays = nextMilestone.days;
    const progress =
      ((currentStreak - startDays) / (endDays - startDays)) * 100;

    return Math.min(100, Math.max(0, progress));
  }
}

export default StreakService;
